// Copy-to-clipboard button: copies the value, confirms with a toast and a check icon for 1.5 s.
import { CheckIcon, CopyIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { notify } from "@/components/ui/sonner";

interface CopyButtonProps extends Omit<ButtonProps, "onClick" | "value"> {
  value: string;
  label?: string;
  toastTitle?: string;
  toastDescription?: string;
}

export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export function CopyButton({
  value,
  label = "Copy",
  toastTitle = "Copied",
  toastDescription,
  variant = "ghost",
  size = "sm",
  ...props
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      setCopied(false);
    }, 1500);
    return () => {
      window.clearTimeout(timer);
    };
  }, [copied]);
  const iconOnly = size === "icon" || size === "icon-sm";
  return (
    <Button
      variant={variant}
      size={size}
      aria-label={iconOnly ? label : undefined}
      onClick={() => {
        void copyText(value).then((ok) => {
          if (ok) {
            setCopied(true);
            notify(toastTitle, toastDescription);
          } else {
            notify("Could not copy", "The browser blocked access to the clipboard.");
          }
        });
      }}
      {...props}
    >
      {copied ? <CheckIcon aria-hidden /> : <CopyIcon aria-hidden />}
      {iconOnly ? null : label}
    </Button>
  );
}
